// admin/src/features/preview/devices.ts
//
// Physical specs for the devices the preview draws at real size. Screens
// are laid out in points (the unit the app and watch use), and one point
// is 25.4 * scale / ppi millimeters on that device. The preview converts
// millimeters to CSS pixels with the calibration in ./calibration.ts.
// Point sizes match the app (440/402/375 pt phones) and the watch's own
// WatchMetrics table (targets/watch/Theme/Theme.swift).

export type DeviceKind = 'phone' | 'watch';

export type DeviceSpec = {
  id: string;
  kind: DeviceKind;
  label: string;
  widthPt: number;
  heightPt: number;
  ppi: number;
  /** Pixels per point (3 on iPhones, 2 on Apple Watch). */
  scale: number;
  /** Outer case, millimeters. */
  bodyMm: { width: number; height: number };
  /** Display corner radius, points. */
  cornerPt: number;
  /** Status-bar inset the app pads under (phones). */
  safeTopPt: number;
  safeBottomPt: number;
  cutout: 'island' | 'notch' | 'none';
};

export const PHONES: readonly DeviceSpec[] = [
  { id: 'pro-max', kind: 'phone', label: 'Pro Max', widthPt: 440, heightPt: 956, ppi: 460, scale: 3, bodyMm: { width: 78.0, height: 163.4 }, cornerPt: 62, safeTopPt: 62, safeBottomPt: 34, cutout: 'island' },
  { id: 'iphone-17', kind: 'phone', label: 'iPhone 17', widthPt: 402, heightPt: 874, ppi: 460, scale: 3, bodyMm: { width: 71.5, height: 149.6 }, cornerPt: 55, safeTopPt: 62, safeBottomPt: 34, cutout: 'island' },
  { id: 'mini', kind: 'phone', label: 'Mini', widthPt: 375, heightPt: 812, ppi: 476, scale: 3, bodyMm: { width: 64.2, height: 131.5 }, cornerPt: 44, safeTopPt: 50, safeBottomPt: 34, cutout: 'notch' },
];

export const WATCHES: readonly DeviceSpec[] = [
  { id: 'watch-46', kind: 'watch', label: '46mm', widthPt: 208, heightPt: 248, ppi: 330, scale: 2, bodyMm: { width: 39, height: 46 }, cornerPt: 50, safeTopPt: 0, safeBottomPt: 0, cutout: 'none' },
  { id: 'watch-42', kind: 'watch', label: '42mm', widthPt: 187, heightPt: 223, ppi: 330, scale: 2, bodyMm: { width: 36, height: 42 }, cornerPt: 44, safeTopPt: 0, safeBottomPt: 0, cutout: 'none' },
  { id: 'watch-ultra', kind: 'watch', label: 'Ultra', widthPt: 211, heightPt: 257, ppi: 338, scale: 2, bodyMm: { width: 44, height: 49 }, cornerPt: 57, safeTopPt: 0, safeBottomPt: 0, cutout: 'none' },
];

/** ISO/IEC 7810 ID-1: every bank, credit and ID card. */
export const CARD_MM = { width: 85.6, height: 53.98, corner: 3.18 } as const;

export function mmPerPt(device: Pick<DeviceSpec, 'ppi' | 'scale'>): number {
  return (25.4 * device.scale) / device.ppi;
}

/** Body size in the device's own points (the frame is drawn in points, then scaled). */
export function bodyPt(device: DeviceSpec): { width: number; height: number } {
  const m = mmPerPt(device);
  return { width: device.bodyMm.width / m, height: device.bodyMm.height / m };
}

/** How much to scale a layout drawn in this device's points so it shows at physical size. */
export function realSizeScale(device: Pick<DeviceSpec, 'ppi' | 'scale'>, cssPxPerMm: number): number {
  return mmPerPt(device) * cssPxPerMm;
}

/** The watch's WatchMetrics.s(): 0.78 (40 mm) ... 1.0 (46 mm) ... ~1.01 (Ultra 3), rounded to whole points. */
export function watchScaled(device: Pick<DeviceSpec, 'widthPt'>, value: number): number {
  const scale = Math.min(1.02, Math.max(0.78, device.widthPt / 208));
  return Math.round(value * scale);
}
